import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFirstSendWarning, type IFirstSendWarningNotice } from "../services/api";
import { useSessionStore } from "../store/useSessionStore";
import { safeCapture } from "../utils/posthog";

/**
 * The notice is requested once per link and kept in memory for the session.
 * It is cleared after an operation is created, because the user is no longer
 * on their first send.
 */
const noticeByLink = new Map<string, Promise<IFirstSendWarningNotice | null>>();
let shownTracked = false;

export const resetFirstSendWarning = () => {
  noticeByLink.clear();
  shownTracked = false;
};

/**
 * True when the first-send warning was displayed during this session. Attached to
 * the operation-created events so the effect of the warning on declines can be measured.
 */
export const wasFirstSendWarningShown = () => shownTracked;

const loadNotice = (linkId: string) => {
  let pending = noticeByLink.get(linkId);
  if (!pending) {
    // On any failure the notice is simply not shown.
    pending = getFirstSendWarning(linkId).catch(() => null);
    noticeByLink.set(linkId, pending);
  }
  return pending;
};

export interface FirstSendWarningText {
  title: string;
  body: string;
}

/**
 * Returns the warning text when the backend flag is on, the user has no
 * previous operations and the entered amount is above the configured threshold.
 */
export const useFirstSendWarning = (
  amount: string,
  sourceCurrency: string
): FirstSendWarningText | null => {
  const { i18n } = useTranslation();
  const linkId = useSessionStore((state) => state.session.data?.linkId);
  const [notice, setNotice] = useState<IFirstSendWarningNotice | null>(null);

  useEffect(() => {
    if (!linkId) return;
    let cancelled = false;
    loadNotice(linkId).then((result) => {
      if (!cancelled) setNotice(result);
    });
    return () => {
      cancelled = true;
    };
  }, [linkId]);

  const numericAmount = amount ? parseFloat(amount) : NaN;
  const applies =
    !!notice?.enabled &&
    !!notice.isFirstSend &&
    !!notice.title &&
    !!notice.body &&
    typeof notice.thresholdAmount === "number" &&
    notice.currency === sourceCurrency &&
    Number.isFinite(numericAmount) &&
    numericAmount > notice.thresholdAmount;

  useEffect(() => {
    if (applies && !shownTracked) {
      shownTracked = true;
      safeCapture("ux.send.transfer.first_send_warning.shown", {
        amount: numericAmount,
        threshold: notice?.thresholdAmount,
        currency: sourceCurrency,
        language: i18n.language?.slice(0, 2) === "es" ? "es" : "en",
      });
    }
  }, [applies, numericAmount, notice?.thresholdAmount, sourceCurrency, i18n.language]);

  if (!applies || !notice?.title || !notice.body) return null;

  const lang = i18n.language?.slice(0, 2) === "es" ? "es" : "en";
  return { title: notice.title[lang], body: notice.body[lang] };
};
